import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import ReactPlayer from "react-player";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { fetchVideoFrames, trimVideo } from "../Services/ShowsApi";
import { useLoader } from "./LoaderContext";

const TrimModal = ({
  open,
  setTrimModalOpen,
  playerRef,
  showURL,
  isPlaying,
  onDuration,
  onProgress,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  duration,
  setDragging,
  roomID,
  mp4File,
  onVideoSaved,
  dragging,
  bucketName,
}) => {
  const [frames, setFrames] = useState([]);
  const [framesLoading, setFramesLoading] = useState(true);
  const canvasRef = useRef(null);
  const [previewTrimModalOpen, setPreviewTrimModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { triggerLoader, hideLoader } = useLoader();

  const extractFrames = useCallback(
    async (videoDuration) => {
      if (!showURL) return;

      setFramesLoading(true);
      setFrames([]);

      try {
        console.log("Fetching frames from backend API...");
        console.log("duraion", videoDuration);
        const inputBody = {
          object_key: mp4File.Key,
          bucketName: bucketName,
        };
        console.log("input body: ", inputBody);
        const response = await fetchVideoFrames(inputBody);
        console.log("response: ", response);

        if (response.status === 200 && response.data.Data) {
          setFrames(response.data.Data);
          console.log("Extracted frames:", response.data.Data);
        } else {
          console.error("Failed to fetch frames: Invalid response format.");
        }
      } catch (error) {
        console.error("Error fetching frames from backend API:", error);
      } finally {
        setFramesLoading(false);
      }
    },
    [showURL]
  );

  const handleTrimModalClose = () => {
    setTrimModalOpen(false);
  };

  useEffect(() => {
    if (open && duration > 0) {
      extractFrames(duration);
    }
  }, [open, duration, extractFrames]);
  const handleTrim = async () => {
    console.log(
      `Trimming video from ${parseInt(startTime)} to ${parseInt(endTime)}`
    );
    if (startTime < 0) {
      setStartTime(0);
    }
    if (endTime > duration) {
      setEndTime(duration);
    }
    setPreviewTrimModalOpen(true);
  };

  const handleTrimPreviewClose = () => {
    setPreviewTrimModalOpen(false);
  };

  const formatTime = (timeInSeconds) => {
    if (!timeInSeconds || isNaN(timeInSeconds) || timeInSeconds < 0)
      return "00:00";

    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  const handleSaveTrimmedVideo = async () => {
    setPreviewTrimModalOpen(false);
    setTrimModalOpen(false);
    triggerLoader("Processing trimming...");
    try {
      const inputBody = {
        startTime: parseInt(startTime),
        endTime: parseInt(endTime),
        roomID,
        mp4file: mp4File,
        bucketName,
        duration,
      };

      console.log("trim video input body: ", inputBody);
      const response = await trimVideo(inputBody);
      console.log("response: ", response);
      hideLoader();
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error during trim:", error);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleTrimModalClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Trim Video
          <IconButton
            aria-label="close"
            onClick={handleTrimModalClose}
            style={{ position: "absolute", right: 16, top: 16 }}
          >
            <DeleteRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <canvas
            id="frameCanvas"
            ref={canvasRef}
            width={200}
            height={100}
            style={{ display: "none" }}
          ></canvas>

          <ReactPlayer
            ref={playerRef}
            url={showURL}
            playing={isPlaying}
            controls
            onDuration={onDuration}
            onProgress={onProgress}
            width="100%"
            height="100%"
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "#fff",
            }}
          >
            <span>Start Time: {formatTime(Math.max(0, startTime))}</span>
            <span>End Time: {formatTime(Math.min(duration, endTime))}</span>
          </div>

          <div
            className="frame-slider-container"
            style={{
              position: "relative",
              height: "100px",
              backgroundColor: "#2a2a2a",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              marginTop: "16px",
            }}
          >
            <div
              className="frames-wrapper"
              style={{
                position: "relative",
                display: "flex",
                flex: 1,
                height: "100%",
                margin: "0 5px",
                overflow: "hidden",
              }}
            >
              {framesLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <CircularProgress size={40} style={{ color: "#fff" }} />
                </Box>
              ) : (
                <div
                  className="frames-container"
                  style={{
                    display: "flex",
                    flex: 1,
                    gap: "1px",
                  }}
                >
                  {frames.map((frame, index) => (
                    <div
                      key={index}
                      className="frame"
                      style={{
                        backgroundImage: `url(${frame})`,
                        backgroundSize: "cover",
                        flex: 1,
                      }}
                    ></div>
                  ))}
                  <div
                    className="highlight-bar"
                    style={{
                      position: "absolute",
                      height: "100%",
                      left: `${(startTime / duration) * 100}%`,
                      width: `${Math.min(
                        ((endTime - startTime) / duration) * 100,
                        (1 - startTime / duration) * 100
                      )}%`,
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  ></div>
                  <div
                    className="handle handle-start"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: `${Math.max(0, (startTime / duration) * 100)}%`,
                      cursor: "ew-resize",
                      width: "10px",
                      height: "100%",
                      backgroundColor: "#00f",
                      zIndex: 10,
                    }}
                    onMouseDown={() => {
                      setDragging("start");
                      if (startTime >= endTime) {
                        setStartTime(Math.max(0, endTime - duration * 0.01));
                      }
                    }}
                  ></div>

                  {/* End Handle */}
                  <div
                    className="handle handle-end"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: `${Math.min((endTime / duration) * 100, 100)}%`,
                      cursor: "ew-resize",
                      width: "10px",
                      height: "100%",
                      backgroundColor: "#00f",
                      zIndex: 10,
                    }}
                    onMouseDown={() => {
                      setDragging("end");
                      if (endTime <= startTime) {
                        setEndTime(
                          Math.min(duration, startTime + duration * 0.01)
                        );
                      }
                    }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTrimModalClose} color="primary" className="cancel-btn">
            <span className="text-trans-none">
              Cancel
            </span>
          </Button>
          <Button
            onClick={() => {
              handleTrim();
            }}
            style={{ color: 'white' }}
            className="join-btn"
          >
            <span className="text-trans-none">
              Trim
            </span>
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={previewTrimModalOpen}
        onClose={handleTrimPreviewClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Confirm to trim the selected video?</DialogTitle>
        <DialogActions>
          <Button onClick={handleTrimPreviewClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveTrimmedVideo} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for showing success message */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Video trimming is in process! please refresh after{" "}
          {parseInt(duration / 360) < 1 ? 1 : parseInt(duration / 360)} minute(s).
        </Alert>
      </Snackbar>
    </div>
  );
};

export { TrimModal };
